import React, { useState, useEffect, useRef } from "react";
import { signup, verifyCode, verifyAccount, setToken, getInstitutePricing, createInstituteCheckout } from "../api";
import { ageFromDob, todayIso } from "../utils/age";
import { track, setCheckoutFlow } from "../utils/analytics";
import { buildSignupOnboardingPayload, getOnboarding } from "../utils/attribution";
import { MOTIVATION_LABELS } from "./onboarding/onboardingContent";

function SignupPage({ onGoToLogin, onGoToReset, onVerified, initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  // Read once per mount: the /start funnel's answer, if they came through it.
  const [motivationEcho] = useState(() => MOTIVATION_LABELS[getOnboarding().motivation] || "");
  const [code, setCode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [voucher, setVoucher] = useState("");
  const [accountType, setAccountType] = useState("individual"); // individual | institute
  const [instituteName, setInstituteName] = useState("");
  const [seats, setSeats] = useState("");
  const [institutePricing, setInstitutePricing] = useState(null); // { min_seats, seat_yearly_price_cents }
  const [step, setStep] = useState(1); // 1=email, 2=code, 3=name+password
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // True when this email already has an abandoned account with a password set —
  // they can log in to jump straight to their free trial instead of redoing signup.
  const [existingAccount, setExistingAccount] = useState(false);
  // True when this email already has a fully active account — show log in / reset
  // options instead of a "check your email" screen that never arrives.
  const [accountActive, setAccountActive] = useState(false);

  // Arriving from the landing page with ?email=… — auto-send the code once and
  // drop the user straight onto the verification step.
  const autoSent = useRef(false);
  useEffect(() => {
    const e = (initialEmail || "").trim();
    if (autoSent.current || !e || !/\S+@\S+\.\S+/.test(e)) return;
    autoSent.current = true;
    (async () => {
      setLoading(true);
      try {
        const res = await signup(e);
        if (res?.account_active) {
          setAccountActive(true);
          return;
        }
        setExistingAccount(!!res?.account_exists);
        setStep(2);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [initialEmail]);

  // Step 1: submit email
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await signup(email.trim());
      if (res?.account_active) {
        setAccountActive(true);
        return;
      }
      setExistingAccount(!!res?.account_exists);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: verify 6-digit code
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await verifyCode(email.trim(), code.trim());
      setStep(3);
    } catch (err) {
      setError(err.message || "Incorrect or expired code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: resend code
  const handleResend = async () => {
    setError(null);
    setLoading(true);
    try {
      await signup(email.trim());
      setCode("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load institute pricing when the final step is reached: it carries the
  // signup_enabled flag (whether to show the Institute account type at all)
  // plus min seats and the per-seat price for the form's numbers.
  useEffect(() => {
    if (step !== 3 || institutePricing) return;
    getInstitutePricing()
      .then((p) => {
        setInstitutePricing(p);
        setSeats((s) => s || String(p.min_seats));
      })
      .catch(() => {});
  }, [step, institutePricing]);
  const instituteSignupEnabled = !!institutePricing?.signup_enabled;

  // Step 3: set name + password. With a voucher, the account is created and
  // unlocked right away (no Stripe). Otherwise we hand off to Stripe Checkout
  // and the account is activated on return (handled in App.js). Institute
  // signups always go through checkout (seats × yearly price, no trial).
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim()) {
      setError("Please enter your first name.");
      return;
    }
    if (!gender) {
      setError("Please select your gender.");
      return;
    }
    if (!dob) {
      setError("Please enter your date of birth.");
      return;
    }
    if (ageFromDob(dob) < 13) {
      setError("You must be at least 13 years old to register.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (accountType === "institute") {
      if (!instituteName.trim()) {
        setError("Please enter your institute's name.");
        return;
      }
      const minSeats = institutePricing?.min_seats || 10;
      if (!seats || parseInt(seats, 10) < minSeats) {
        setError(`Institutes must purchase at least ${minSeats} seats.`);
        return;
      }
    }

    const common = {
      email: email.trim(),
      code: code.trim(),
      password,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      gender,
      date_of_birth: dob,
    };
    // Attribution + /start answers staged in localStorage; the backend writes
    // them onto the profile before the Stripe redirect, so they survive the
    // round-trip server-side. Not cleared here — the first-run welcome clears
    // them after signup completes (a canceled checkout may come back).
    const onboarding = buildSignupOnboardingPayload();
    if (onboarding) common.onboarding = onboarding;

    setLoading(true);
    try {
      if (accountType === "institute") {
        const { url } = await createInstituteCheckout({
          ...common,
          institute_name: instituteName.trim(),
          seats: parseInt(seats, 10),
        });
        setCheckoutFlow("institute");
        window.location.assign(url);
        return;
      }
      if (voucher.trim()) {
        // Voucher path: create + activate the account and redeem the voucher,
        // then log straight in (no payment).
        const data = await verifyAccount({ ...common, voucher: voucher.trim() });
        if (data.token) setToken(data.token);
        track("signup", { method: "voucher" });
        onVerified();
        return;
      }
      // Same call, without a voucher: the backend completes the account and
      // grants the free trial itself. Signup never touches Stripe — a payment
      // page mid-signup reads as "this is a payment site", and there is nothing
      // to pay for yet. The card comes later, at the paywall.
      const data = await verifyAccount(common);
      if (data.token) setToken(data.token);
      // Fired inline now. These used to be staged in sessionStorage and
      // replayed after the Stripe round-trip (queueCheckoutEvents), which no
      // longer happens — keep both so the funnel metric is unbroken.
      track("signup", { method: "trial" });
      track("trial-started");
      onVerified();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fmtDollars = (cents) => (cents / 100).toFixed(2).replace(/\.00$/, "");
  // Seats are priced at a discounted monthly rate but billed yearly up front (12×).
  const seatMonthlyDollars = institutePricing
    ? fmtDollars(institutePricing.seat_monthly_price_cents)
    : null;
  const seatYearlyDollars = institutePricing
    ? fmtDollars(institutePricing.seat_yearly_price_cents)
    : null;
  const instituteTotalDollars =
    institutePricing && seats && parseInt(seats, 10) > 0
      ? fmtDollars(institutePricing.seat_yearly_price_cents * parseInt(seats, 10))
      : null;

  return (
    <div className="dark-scope relative min-h-screen flex items-center justify-center px-4 py-10 overflow-hidden" style={{ background: "var(--ink)" }}>
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[rgba(201,162,39,0.07)] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-24 w-96 h-96 rounded-full bg-[rgba(62,134,134,0.10)] blur-3xl" />
      <div className="relative z-10 w-full max-w-sm animate-rise">
        {/* Logo lockup */}
        <div className="text-center mb-7">
          <div className="inline-flex flex-col items-center">
            <span className="text-6xl font-arabic-display text-dt-gold-bright leading-none mb-2" dir="rtl">
              مراجعة
            </span>
            <div className="rule-ornament w-40 text-sm">
              <span className="text-gold-400">✦</span>
            </div>
            <p className="mt-2 text-xs uppercase tracking-[0.25em] text-dt-gold-dim font-medium">
              murājaʿa
            </p>
          </div>
        </div>
        <div className="surface-glass rounded-2xl shadow-soft-lg p-8">
          {/* Continuity echo for visitors arriving from the /start funnel */}
          {!accountActive && motivationEcho && (
            <p className="text-center text-xs text-dt-gold-dim mb-4">
              ✦ Your goal: {motivationEcho}
            </p>
          )}
          {/* Step indicator (hidden once we know the account already exists) */}
          {!accountActive && (
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map((n) => (
                <span
                  key={n}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    step === n ? "w-8 bg-[var(--gold)]" : step > n ? "w-8 bg-[var(--gold-dim)]" : "w-4 bg-[var(--forest-softer)]"
                  }`}
                />
              ))}
            </div>
          )}

        {/* Email already has an active account → offer log in / reset */}
        {accountActive && (
          <>
            <h2 className="text-2xl font-display font-semibold text-ink-800 mb-2">You already have an account</h2>
            <p className="text-sm text-ink-500 mb-6">
              An account for <strong>{email.trim()}</strong> already exists. Log in to continue,
              or reset your password if you've forgotten it.
            </p>
            <div className="space-y-3">
              <button
                onClick={onGoToLogin}
                className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink font-semibold text-sm hover:brightness-105 shadow-soft transition-colors"
              >
                Log in
              </button>
              <button
                onClick={() => onGoToReset && onGoToReset(email.trim())}
                className="w-full py-2.5 rounded-xl border border-parchment-300 bg-white/70 text-ink-600 font-medium text-sm hover:bg-parchment-200 transition-colors"
              >
                Reset my password
              </button>
              <button
                onClick={() => { setAccountActive(false); setError(null); }}
                className="w-full py-2 text-slate-400 hover:text-slate-600 text-sm transition-colors"
              >
                Use a different email
              </button>
            </div>
          </>
        )}

        {!accountActive && step === 1 && (
          <>
            <h2 className="text-2xl font-display font-semibold text-ink-800 mb-6">Create account</h2>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                  Email address
                </label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                />
              </div>

              {error && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink font-semibold text-sm hover:brightness-105 disabled:opacity-50 shadow-soft transition-colors"
              >
                {loading ? "Sending…" : "Send verification email"}
              </button>
            </form>

            <p className="mt-4 text-sm text-slate-500 text-center">
              Already have an account?{" "}
              <button
                onClick={onGoToLogin}
                className="text-ink-600 hover:text-ink-700 font-semibold underline-offset-2 hover:underline"
              >
                Log in
              </button>
            </p>
          </>
        )}

        {/* Step 2: Code entry */}
        {step === 2 && (
          <>
            <h2 className="text-lg font-semibold text-slate-700 mb-2">Check your email</h2>
            <p className="text-sm text-slate-500 mb-6">
              We sent a 6-digit code to <strong>{email}</strong>. Enter it below. The code
              expires in 10 minutes.
            </p>

            {existingAccount && (
              <div className="mb-5 rounded-xl border border-gold-300 bg-gold-50 px-4 py-3 text-sm text-ink-700">
                <p className="mb-2">
                  You already started signing up with this email. Log in with your password to
                  go straight to your free trial — no need to verify again.
                </p>
                <button
                  type="button"
                  onClick={onGoToLogin}
                  className="font-semibold text-ink-700 underline underline-offset-2 hover:text-ink-800"
                >
                  Log in instead
                </button>
              </div>
            )}

            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="w-full px-4 py-4 rounded-xl border border-parchment-300 bg-white/80 text-3xl text-center font-mono tracking-[0.4em] text-ink-800 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                />
              </div>

              {error && (
                <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink font-semibold text-sm hover:brightness-105 disabled:opacity-50 shadow-soft transition-colors"
              >
                {loading ? "Verifying…" : "Verify code"}
              </button>
            </form>

            <p className="mt-4 text-sm text-slate-500 text-center">
              Prefer to use the link? Check your email for the verification link instead.
            </p>

            <button
              onClick={handleResend}
              disabled={loading}
              className="mt-2 w-full py-2.5 rounded-xl border border-parchment-300 bg-white/70 text-ink-600 font-medium text-sm hover:bg-parchment-200 disabled:opacity-50 transition-colors"
            >
              {loading ? "Sending…" : "Resend code"}
            </button>
          </>
        )}

        {/* Step 3: Name + password setup */}
        {step === 3 && (
          <>
            <h2 className="text-2xl font-display font-semibold text-ink-800 mb-6">Almost done!</h2>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {instituteSignupEnabled && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                  Account type
                </label>
                <div className="flex gap-3">
                  {[
                    { value: "individual", label: "Individual" },
                    { value: "institute", label: "Institute / School" },
                  ].map(({ value, label }) => (
                    <label
                      key={value}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm font-medium transition-colors ${
                        accountType === value
                          ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                          : "border-slate-300 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="accountType"
                        value={value}
                        checked={accountType === value}
                        onChange={() => setAccountType(value)}
                        className="sr-only"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                {accountType === "institute" && (
                  <p className="mt-2 text-xs text-slate-400">
                    Buy seats for your students at a discounted yearly price. You'll manage
                    student and teacher accounts. Your institute is reviewed before activation.
                  </p>
                )}
              </div>
              )}

              {instituteSignupEnabled && accountType === "institute" && (
                <>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                      Institute name
                    </label>
                    <input
                      type="text"
                      required
                      autoComplete="organization"
                      value={instituteName}
                      onChange={(e) => setInstituteName(e.target.value)}
                      placeholder="Dar al-Arabiyya"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                      Number of seats
                    </label>
                    <input
                      type="number"
                      required
                      min={institutePricing?.min_seats || 10}
                      value={seats}
                      onChange={(e) => setSeats(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                    />
                    {institutePricing && (
                      <p className="mt-1.5 text-xs text-slate-400">
                        ${seatMonthlyDollars}/month per seat (${seatYearlyDollars}/seat billed yearly),
                        minimum {institutePricing.min_seats} seats
                        {instituteTotalDollars && (
                          <> — <span className="font-semibold text-ink-600">${instituteTotalDollars}/year total</span></>
                        )}
                        . Each student or teacher account uses one seat.
                      </p>
                    )}
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                    First name
                  </label>
                  <input
                    type="text"
                    required
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Ali"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                    Last name
                  </label>
                  <input
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Hassan"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Gender <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-slate-400 mb-2">
                  Practice calls are gender-segregated. This cannot be changed after signup.
                </p>
                <div className="flex gap-3">
                  {[{ value: "male", label: "Male" }, { value: "female", label: "Female" }].map(({ value, label }) => (
                    <label
                      key={value}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm font-medium transition-colors ${
                        gender === value
                          ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                          : "border-slate-300 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="gender"
                        value={value}
                        checked={gender === value}
                        onChange={() => setGender(value)}
                        className="sr-only"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Date of birth <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-slate-400 mb-2">
                  Some features (such as practice calls) may require you to be 18 or over.
                </p>
                <input
                  type="date"
                  required
                  autoComplete="bday"
                  value={dob}
                  max={todayIso()}
                  onChange={(e) => setDob(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Choose a password"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                  Confirm password
                </label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                />
              </div>

              {accountType === "individual" && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                    Voucher code <span className="text-ink-300 normal-case font-normal tracking-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={voucher}
                    onChange={(e) => setVoucher(e.target.value.toUpperCase())}
                    placeholder="Have a code? Enter it to skip payment"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base font-mono text-ink-800 placeholder:text-ink-300 placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                  />
                </div>
              )}

              {error && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink font-semibold text-sm hover:brightness-105 disabled:opacity-50 shadow-soft transition-colors"
              >
                {accountType === "institute"
                  ? (loading ? "Starting checkout…" : "Continue to payment")
                  : loading
                  ? (voucher.trim() ? "Redeeming…" : "Starting your trial…")
                  : (voucher.trim() ? "Redeem voucher & start" : "Start my 7-day free trial")}
              </button>

              <p className="text-xs text-slate-400 text-center">
                {accountType === "institute"
                  ? "You'll pay for the full year up front. After payment your institute is reviewed — you'll be able to add students once it's approved."
                  : voucher.trim()
                  ? "Your voucher unlocks full access — no card and no charge."
                  : "No card required. Enjoy 7 days free — when the trial ends you can subscribe to keep going, or simply walk away. Nothing is charged now."}
              </p>
            </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
